import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import { 
  TrendingUp, DollarSign, FileText, RefreshCw, 
  PieChart as PieIcon, BarChart2, AlertTriangle,
  Lightbulb, Bell, History
} from 'lucide-react';
import SpendTrends from './SpendTrends';
import InsightsDashboard from './InsightsDashboard';
import RemindersPanel from './RemindersPanel';
import AuditLogViewer from './AuditLogViewer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#4338CA', '#7C3AED', '#EC4899', '#F59E0B', '#10B981', '#06B6D4', '#8B5CF6', '#F97316'];

function AnalyticsDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [budgetStatus, setBudgetStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');

  useEffect(() => {
    fetchAnalytics();
    fetchBudgetStatus();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics/dashboard`, { withCredentials: true });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBudgetStatus = async () => {
    try {
      const response = await axios.get(`${API}/settings/budget/status`, { withCredentials: true });
      setBudgetStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch budget status:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Budget Alert */}
      {budgetStatus?.has_budget && budgetStatus.is_over_threshold && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded-xl flex items-start space-x-3 ${
            budgetStatus.is_over_limit 
              ? 'bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-700' 
              : 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700'
          }`}
        >
          <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${
            budgetStatus.is_over_limit ? 'text-rose-600' : 'text-amber-600'
          }`} />
          <div>
            <p className={`font-medium ${budgetStatus.is_over_limit ? 'text-rose-900 dark:text-rose-200' : 'text-amber-900 dark:text-amber-200'}`}>
              {budgetStatus.is_over_limit ? 'Budget Exceeded!' : 'Budget Alert'}
            </p>
            <p className={`text-sm ${budgetStatus.is_over_limit ? 'text-rose-700 dark:text-rose-300' : 'text-amber-700 dark:text-amber-300'}`}>
              You've spent ${budgetStatus.current_spent.toLocaleString()} of your ${budgetStatus.monthly_limit.toLocaleString()} monthly budget ({budgetStatus.percentage}%)
            </p>
          </div>
        </motion.div>
      )}

      {/* Section Navigation */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-1.5 overflow-x-auto">
        <div className="flex space-x-1 min-w-max">
          {[
            { id: 'overview', label: 'Overview', icon: BarChart2 },
            { id: 'trends', label: 'Trends & Forecast', icon: TrendingUp },
            { id: 'insights', label: 'AI Insights', icon: Lightbulb },
            { id: 'reminders', label: 'Reminders', icon: Bell },
            { id: 'activity', label: 'Activity Log', icon: History }
          ].map(section => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${
                activeSection === section.id
                  ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
              data-testid={`analytics-section-${section.id}`}
            >
              <section.icon className="w-4 h-4" />
              {section.label}
            </button>
          ))}
        </div>
      </div>

      {/* Overview Section */}
      {activeSection === 'overview' && analytics && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4"
            >
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-indigo-700 dark:text-indigo-400" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Total Spent</p>
                  <p className="text-lg font-bold text-slate-900 dark:text-white">${analytics.total_amount.toLocaleString()}</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4"
            >
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-emerald-700 dark:text-emerald-400" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Invoices</p>
                  <p className="text-lg font-bold text-slate-900 dark:text-white">{analytics.total_count}</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4"
            >
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center">
                  <RefreshCw className="w-5 h-5 text-purple-700 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Recurring</p>
                  <p className="text-lg font-bold text-slate-900 dark:text-white">{analytics.recurring_count}</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4"
            >
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-amber-700 dark:text-amber-400" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Categories</p>
                  <p className="text-lg font-bold text-slate-900 dark:text-white">{analytics.categories.length}</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Monthly Spending Chart */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4 sm:p-6"
            >
              <div className="flex items-center space-x-2 mb-4">
                <BarChart2 className="w-5 h-5 text-indigo-700 dark:text-indigo-400" />
                <h3 className="font-semibold text-slate-900 dark:text-white">Monthly Spending</h3>
              </div>
              {analytics.months.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={analytics.months}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#64748B" />
                    <YAxis tick={{ fontSize: 12 }} stroke="#64748B" tickFormatter={(v) => `$${v}`} />
                    <Tooltip 
                      formatter={(value) => [`$${value.toLocaleString()}`, 'Amount']}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0' }}
                    />
                    <Bar dataKey="amount" fill="#4338CA" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[250px] flex items-center justify-center text-slate-400">
                  No monthly data yet
                </div>
              )}
            </motion.div>

            {/* Category Breakdown */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4 sm:p-6"
            >
              <div className="flex items-center space-x-2 mb-4">
                <PieIcon className="w-5 h-5 text-indigo-700 dark:text-indigo-400" />
                <h3 className="font-semibold text-slate-900 dark:text-white">By Category</h3>
              </div>
              {analytics.categories.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={analytics.categories}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {analytics.categories.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[250px] flex items-center justify-center text-slate-400">
                  No category data yet
                </div>
              )}
            </motion.div>
          </div>

          {/* Top Vendors */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4 sm:p-6"
          >
            <h3 className="font-semibold text-slate-900 dark:text-white mb-4">Top Vendors</h3>
            {analytics.vendors.length > 0 ? (
              <div className="space-y-3">
                {analytics.vendors.slice(0, 5).map((vendor, index) => (
                  <div key={vendor.name} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <span className="w-6 h-6 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-400 text-xs font-bold flex items-center justify-center">
                        {index + 1}
                      </span>
                      <span className="text-sm text-slate-700 dark:text-slate-300 truncate max-w-[200px]">{vendor.name}</span>
                    </div>
                    <span className="text-sm font-semibold text-slate-900 dark:text-white">${vendor.value.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400 text-center py-4">No vendor data yet</p>
            )}
          </motion.div>
        </motion.div>
      )}

      {/* Trends & Forecast Section */}
      {activeSection === 'trends' && <SpendTrends />}

      {/* AI Insights Section */}
      {activeSection === 'insights' && <InsightsDashboard />}

      {/* Reminders Section */}
      {activeSection === 'reminders' && <RemindersPanel />}

      {/* Activity Log Section */}
      {activeSection === 'activity' && <AuditLogViewer />}
    </div>
  );
}

export default AnalyticsDashboard;
